/**
 * Safe pay buttons for the Marketplace.
 *
 * THE RULE: the site builds every payment link itself, from the seller's
 * handle, using one fixed address per app. It never shows a link a seller
 * pasted. A pasted link could lead to a fake payment page that steals card
 * details, and the people buying here include elderly relatives.
 *
 * So a handle is cleaned up (a leading @ or $, or a full link to the SAME app,
 * is reduced to the bare name) and then checked against the characters that
 * app allows. Anything else -- another website, "javascript:", spaces, odd
 * symbols -- gets no button at all; the handle is shown as plain text instead.
 *
 * The site never processes money. The button only opens the seller's page in
 * that app, where the app's own security checks apply.
 */

export const PAYMENT_PLATFORMS = [
  { value: "venmo", label: "Venmo" },
  { value: "cashapp", label: "Cash App" },
  { value: "paypal", label: "PayPal" },
  { value: "zelle", label: "Zelle" },
  { value: "cash", label: "Cash" },
  { value: "check", label: "Check" },
];

export function platformLabel(value) {
  const found = PAYMENT_PLATFORMS.find((p) => p.value === value);
  return found ? found.label : null;
}

// One entry per app that has a public pay page. Nothing outside this list can
// ever become a link.
const LINKABLE = {
  venmo: {
    // Venmo usernames: letters, numbers, dashes and underscores.
    pattern: /^[A-Za-z0-9_-]{1,30}$/,
    prefix: "@",
    hosts: ["venmo.com", "www.venmo.com", "account.venmo.com"],
    // venmo.com/u/name or venmo.com/name
    pathPattern: /^\/(?:u\/)?([^/]+)\/?$/,
    build: (h) => `https://venmo.com/u/${h}`,
    display: (h) => `@${h}`,
    // A phone number is a real way to find someone on Venmo, but it is not a
    // username, so it cannot make a working link. Show it as text instead.
    reject: (h) => /^[0-9-]+$/.test(h),
  },
  cashapp: {
    // $Cashtags: letters and numbers, at least one letter, up to 20.
    pattern: /^[A-Za-z0-9]{1,20}$/,
    prefix: "$",
    hosts: ["cash.app", "www.cash.app"],
    pathPattern: /^\/\$([^/]+)\/?$/,
    build: (h) => `https://cash.app/$${h}`,
    display: (h) => `$${h}`,
    reject: (h) => !/[A-Za-z]/.test(h),
  },
  paypal: {
    // PayPal.Me names: letters and numbers, up to 20.
    pattern: /^[A-Za-z0-9]{1,20}$/,
    prefix: "@",
    hosts: ["paypal.me", "www.paypal.me", "paypal.com", "www.paypal.com"],
    // paypal.me/name, paypal.me/name/25 (an amount), paypal.com/paypalme/name
    pathPattern: /^\/(?:paypalme\/)?([^/]+)(?:\/[0-9.]+)?\/?$/,
    build: (h) => `https://paypal.me/${h}`,
    display: (h) => h,
    reject: () => false,
  },
};

export function isLinkablePlatform(platform) {
  return typeof platform === "string" && Object.prototype.hasOwnProperty.call(LINKABLE, platform);
}

// Look a platform up by its OWN entries only, so a name like "__proto__" or
// "toString" can never pick up something that is not in the list above.
function ruleFor(platform) {
  return isLinkablePlatform(platform) ? LINKABLE[platform] : null;
}

/**
 * If `raw` is a full link, return the bare handle ONLY when the link points at
 * this platform's own site; otherwise return null. If it is not a link, return
 * it unchanged.
 */
function handleFromPastedLink(rule, raw, platform) {
  // Anything with a colon, a slash or a dot is not a bare username. Treat it
  // as a link, and accept it only if it is plainly this app's own address.
  if (!/[:/.]/.test(raw)) return raw;

  let text = raw;
  if (!/^[a-z][a-z0-9+.-]*:/i.test(text)) {
    // "venmo.com/u/name" with no https:// in front
    text = `https://${text}`;
  }
  let url;
  try {
    url = new URL(text);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  if (url.username || url.password || url.port) return null;
  if (!rule.hosts.includes(url.hostname.toLowerCase())) return null;
  // Anything after "?" or "#" is ignored: only the name is taken from the
  // path, and the site rebuilds the link from that name alone.
  const path = url.pathname;
  const match = path.match(rule.pathPattern);
  if (!match) return null;
  let name;
  try {
    name = decodeURIComponent(match[1]);
  } catch {
    return null;
  }
  // PayPal's /paypalme/ prefix and Venmo's /u/ must not be read as a name.
  if (platform === "paypal" && name.toLowerCase() === "paypalme") return null;
  if (platform === "venmo" && name.toLowerCase() === "u") return null;
  return name;
}

/**
 * Clean up a seller's handle for a platform that has a pay page.
 * Returns the bare, safe handle, or null if no safe link can be made from it.
 */
export function normalizeHandle(platform, raw) {
  const rule = ruleFor(platform);
  if (!rule || typeof raw !== "string") return null;

  let handle = raw.trim();
  if (!handle || handle.length > 200) return null;

  handle = handleFromPastedLink(rule, handle, platform);
  if (handle === null) return null;

  handle = handle.trim();
  // Strip ONE leading @ or $ (people type "@name" for Venmo, "$name" for Cash App).
  if (handle.startsWith("@") || handle.startsWith("$")) {
    handle = handle.slice(1);
  }

  if (!rule.pattern.test(handle)) return null;
  if (rule.reject(handle)) return null;
  return handle;
}

/**
 * Build the pay button for a listing.
 * Returns { url, label, display } or null when no safe button can be made.
 */
export function buildPayLink(platform, rawHandle) {
  const rule = ruleFor(platform);
  if (!rule) return null;
  const handle = normalizeHandle(platform, rawHandle);
  if (!handle) return null;
  // The handle has already passed a letters/numbers/_/- check, so encoding
  // changes nothing -- it is here as a second lock on the same door.
  const url = rule.build(encodeURIComponent(handle));
  return { url, label: platformLabel(platform), display: rule.display(handle) };
}

// What to tell a seller whose handle can't be used, per app. The server
// (flask_app/api/marketplace/marketplace_item/post.py) checks the same rules.
const HANDLE_HINTS = {
  venmo: "That doesn't look like a Venmo username; enter just the name, e.g. jane-doe",
  cashapp: "That doesn't look like a Cash App $Cashtag; enter just the name, e.g. JaneDoe",
  paypal: "That doesn't look like a PayPal.Me name; enter just the name, e.g. JaneDoe",
};
const MAX_NOTE_LENGTH = 100;
// A plain shape check, not proof the mailbox exists (same as the server).
const EMAIL_PATTERN = /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/;
// Invisible control characters (only ever pasted). The server treats some of
// them as spaces, so they are refused here to keep the two checks in step.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f-\u009f]/;
const isEmail = (text) => text.length <= 254 && !CONTROL_CHARS.test(text) && EMAIL_PATTERN.test(text);
// A US phone number: 10 digits, optionally +1 / 1 in front, usual separators.
const US_PHONE_PATTERN = /^(\+?1[ .-]?)?(\(\d{3}\)|\d{3})[ .-]?\d{3}[ .-]?\d{4}$/;

/**
 * Check the seller's contact email before it is posted, with the server's rule.
 * Returns a message to show under the field, or null (empty is handled as
 * "required" by the form itself).
 */
export function sellerEmailError(raw) {
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  if (!trimmed) return null;
  return isEmail(trimmed)
    ? null
    : "Enter a full email address, e.g. jane@example.com";
}

/**
 * Check a seller's payment handle before it is posted.
 * Returns a friendly message to show under the field, or null when it is fine
 * (an empty handle is fine: payment info is optional).
 */
export function paymentHandleError(platform, raw) {
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  if (!platform || !trimmed) return null;
  if (isLinkablePlatform(platform)) {
    return normalizeHandle(platform, trimmed) ? null : HANDLE_HINTS[platform];
  }
  if (platform === "zelle") {
    if (trimmed.length > MAX_NOTE_LENGTH) {
      return `Please keep this to ${MAX_NOTE_LENGTH} characters or fewer.`;
    }
    return isEmail(trimmed) || US_PHONE_PATTERN.test(trimmed)
      ? null
      : "Enter the email address or US phone number you use with Zelle, e.g. jane@example.com or 555-123-4567";
  }
  if (platform === "cash" || platform === "check") {
    const lowered = trimmed.toLowerCase();
    // eslint-disable-next-line no-control-regex
    if (/[\u0000-\u001f\u007f]/.test(trimmed)) return "Please use plain words only in this note.";
    if (lowered.includes("http") || lowered.includes("www.") || lowered.includes("://")) {
      return "This note can't contain a link. Please use plain words only.";
    }
    if (trimmed.length > MAX_NOTE_LENGTH) {
      return `Please keep this note to ${MAX_NOTE_LENGTH} characters or fewer.`;
    }
  }
  return null;
}

/**
 * The "email the seller" link, or null when the saved address isn't a plain
 * email (an older listing could hold anything). The characters that would
 * start extra mail fields (?, &, #, %) are escaped so the address can only
 * ever be an address.
 */
export function sellerMailto(email, title) {
  const address = typeof email === "string" ? email.trim() : "";
  if (!isEmail(address)) return null;
  const safe = address.replace(/[%?&#]/g, (ch) => encodeURIComponent(ch));
  return `mailto:${safe}?subject=${encodeURIComponent(`Interested in: ${title || ""}`)}`;
}

// Shared by the posting form and the seller's edit form.
export const HANDLE_LABELS = {
  venmo: "Your Venmo username (like @your-name)",
  cashapp: "Your Cash App $Cashtag (like $YourName)",
  paypal: "Your PayPal.Me name (like YourName)",
  zelle: "Your Zelle email or phone number",
  cash: "Any note for buyers (optional)",
  check: "Who to make the check out to (optional)",
};

// What the seller will see under the handle box, so they know exactly what
// buyers get before they post.
export function handlePreview(platform, handle) {
  if (!platform || !handle.trim()) return null;
  if (isLinkablePlatform(platform)) {
    const link = buildPayLink(platform, handle);
    if (link) {
      return `Buyers will see a "Pay with ${link.label}" button that opens ${link.display} in the ${link.label} app.`;
    }
    // No safe link: paymentHandleError says so under the field instead.
    return null;
  }
  if (platform === "zelle") {
    return "Zelle has no public payment link, so buyers will see this with a Copy button.";
  }
  return null;
}
